import { PlexUserRelationState } from '@all-hands-hq/plex-graphql-schema';
import {
  Button,
  createStyles,
  IconButton,
  IconButtonProps,
  List,
  ListItem,
  ListItemSecondaryAction,
  makeStyles,
  Theme,
} from '@material-ui/core';
import CheckBoxCheckedIconMUI from '@material-ui/icons/CheckBox';
import CheckBoxUncheckedIconMUI from '@material-ui/icons/CheckBoxOutlineBlank';
import classNames from 'classnames';
import React, { useState } from 'react';

import { ReactComponent as CaretDownSvg } from '../../../../../assets/caret_down.svg';
import { ReactComponent as CaretUpSvg } from '../../../../../assets/caret_up.svg';
import { PlexSnackbarFragment } from '../../../../../graphql/gen/types';
import { selectAllPlexesFilterItem } from '../../../../../models/filters/actions/selectAllPlexesFilterItem';
import { selectOnlyOnePlexFilterItem } from '../../../../../models/filters/actions/selectOnlyOnePlexFilterItem';
import { selectPlexFilterItem } from '../../../../../models/filters/actions/selectPlexFilterItem';
import { showPlexSummary } from '../../../../../models/filters/actions/showPlexSummary';
import { useFiltersState } from '../../../../../models/filters/useFiltersState';
import { ReduxPlex } from '../../../../../models/ReduxPlex';
import { colorPalette, spacings, TextSizesEnum } from '../../../../../theme';
import { PlexTypography } from '../../../../shared/primitives/PlexTypography';
import { PlexTypographyEllipsed } from '../../../../shared/primitives/PlexTypographyEllipsed';
import { useDialogState } from '../../../dialog/useDialogState';
import {
  canCreateBoard,
  canCreateFact,
  canCreatePost,
  canLeave,
  canManage,
  canViewPlexAnalytics,
} from '../../../permissions';
import { useSubscribeToPlex } from '../../../plexes/useSnackbarSubscribe';
import { MenuOptionItem, MoreOptionsMenu } from '../../../shared/MoreOptionsMenu';
import { EmptySubscriptionsList } from './EmptySubscriptionsList';
import { EmptyTeamPlexesList } from './EmptyTeamPlexesList';

type Props = {
  title: string;
  plexes: ReduxPlex[];
  mode: 'subscriptions' | 'team_plexes';
};

export const PlexesList = React.memo(
  (props: Props): React.ReactElement => {
    const classes = useStyles();
    const { plexes, title, mode } = props;
    const [hoveredId, setHoveredId] = useState('');
    const [, dispatch] = useFiltersState();
    const [, setDialogState] = useDialogState();
    const { onLeaveClick } = useSubscribeToPlex();
    const [collapsed, setCollapsed] = useState(false);

    const handleSelect = (permalink: string, selected: boolean): void => {
      dispatch(selectPlexFilterItem({ permalink, selected }));
    };

    const handleSelectAll = (selected: boolean): void => {
      dispatch(selectAllPlexesFilterItem({ selected, plexPermalinks: new Set(plexes.map((p) => p.permalink)) }));
    };

    const handleSelectOnePlex = (permalink: string, selected: boolean): void => {
      dispatch(selectOnlyOnePlexFilterItem({ selected, permalink }));
    };

    const handleView = (id: string): void => {
      const permalink = plexes.find((plex) => plex.id === id)?.permalink;

      if (permalink != null) {
        dispatch(showPlexSummary({ permalink }));
      }
    };

    const handleEdit = (id: string): void => {
      setDialogState({ type: 'edit_plex', plexId: id });
    };

    const handleNewPost = (id: string): void => {
      setDialogState({ type: 'new_post', plexId: id });
    };

    const handleNewBoard = (id: string): void => {
      setDialogState({ type: 'new_board', plexId: id });
    };

    const handleNewEvent = (id: string): void => {
      setDialogState({ type: 'new_event', plexId: id });
    };

    const handleNewFact = (plex: PlexSnackbarFragment) => (id: string): void => {
      setDialogState({ type: 'new_fact', boardId: plex.defaultBoardId });
    };

    const handleNewCard = (plex: PlexSnackbarFragment) => (id: string): void => {
      setDialogState({ type: 'new_card', boardId: plex.defaultBoardId });
    };

    const handleLeave = (plex: PlexSnackbarFragment) => (id: string): void => {
      onLeaveClick(plex);
    };

    const handleOpenAnalytics = (plexId: string): void => {
      setDialogState({ type: 'plex_analytics', plexId });
    };

    return (
      <List dense component={'nav'}>
        <ListItem
          button
          className={classes.titleHeaderContainer}
          onClick={(): void => {
            const selectedItems = plexes.filter((p) => p.selected).length;
            handleSelectAll(selectedItems < plexes.length);
          }}
        >
          <PlexTypography className={classes.titleHeader} text={title} size={TextSizesEnum.Normal} />
          <ListItemSecondaryAction classes={{ root: classes.collapseButtonContainer }}>
            <Button
              className={classes.collapseButton}
              onClick={(): void => {
                setCollapsed(!collapsed);
              }}
            >
              {collapsed ? <CaretUpSvg /> : <CaretDownSvg />}
            </Button>
          </ListItemSecondaryAction>
        </ListItem>
        {!collapsed &&
          (plexes.length === 0 ? (
            mode === 'subscriptions' ? (
              <EmptySubscriptionsList />
            ) : (
              <EmptyTeamPlexesList />
            )
          ) : (
            plexes.map((plex) => (
              <ListItem
                key={plex.id}
                className={classes.listItem}
                disableGutters={true}
                onMouseEnter={(event): void => {
                  setHoveredId(plex.id);
                }}
                onMouseLeave={(event): void => {
                  setHoveredId('');
                }}
              >
                <Button
                  className={classes.plexNameButton}
                  onClick={(): void => {
                    const selectedItemsCount = plexes.filter((p) => p.selected).length;

                    if (selectedItemsCount > 1) {
                      // if multiple plexes are selected, select it and deselect the others
                      handleSelectOnePlex(plex.permalink, true);
                    } else {
                      // toggle the selection of the current plex and deselect others
                      handleSelectOnePlex(plex.permalink, !plex.selected);
                    }
                  }}
                >
                  <PlexTypographyEllipsed
                    className={classNames(
                      plex.notificationsCount > 0 && classes.plexNameUnreadNotifications,
                      classes.plexName
                    )}
                    text={plex.name}
                    tooltip={plex.name}
                    size={TextSizesEnum.Normal}
                  />
                  {/* TODO(https://github.com/all-hands-hq/source/issues/678) */}
                  {/* <NotificationBadge className={classes.notificationBadge} count={plex.notificationsCount} /> */}
                </Button>

                {hoveredId === plex.id ? (
                  <MoreOptionsMenu
                    id={plex.id}
                    className={classes.moreButton}
                    options={moreOptions({
                      currentUserRelation: plex.currentUserRelation.state,
                      onViewClick: handleView,
                      onEditClick: handleEdit,
                      onNewPostClick: handleNewPost,
                      onNewEventClick: handleNewEvent,
                      onNewFactClick: handleNewFact(plex),
                      onNewBoard: handleNewBoard,
                      onLeaveClick: handleLeave(plex),
                      onOpenAnalytics: handleOpenAnalytics,
                      onNewCardClick: handleNewCard(plex),
                    })}
                    Button={MoreOptionsButton}
                    menuProps={{ anchorOrigin: { horizontal: 'left', vertical: 'bottom' } }}
                  />
                ) : (
                  plex.color && <div className={classes.plexColor} style={{ backgroundColor: plex.color }} />
                )}
                <Button
                  className={classes.checkBoxButton}
                  onClick={(): void => {
                    handleSelect(plex.permalink, !plex.selected);
                  }}
                >
                  {plex.selected ? (
                    <CheckBoxCheckedIconMUI className={classes.checkBoxChecked} />
                  ) : (
                    <CheckBoxUncheckedIconMUI className={classes.checkBoxUnchecked} />
                  )}
                </Button>
              </ListItem>
            ))
          ))}
      </List>
    );
  }
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    collapseButtonContainer: {
      right: 'unset',
      left: 0,
      marginLeft: spacings.normal,
    },
    collapseButton: {
      display: 'flex',
      minWidth: 32,
    },
    titleHeaderContainer: {
      paddingRight: spacings.xsmall,
      paddingLeft: spacings.xlarge,
      height: 32,
      flexDirection: 'row',
      alignItems: 'center',
    },
    titleHeader: {
      color: colorPalette.purpleGray._500,
      fontWeight: 'bold',
      marginLeft: spacings.xxlarge + spacings.small,
    },
    listItem: {
      display: 'flex',
      flexDirection: 'row',
      height: 32,
      justifyContent: 'space-between',
    },
    plexNameButton: {
      display: 'flex',
      width: '100%',
      paddingLeft: spacings.xlarge,
      paddingRight: 48,
      borderRadius: 0,
      justifyContent: 'flex-start',
    },
    plexName: {
      color: '#8181A5',
      fontWeight: 500,
      marginLeft: spacings.xlarge + spacings.normal,
    },
    plexNameUnreadNotifications: {
      fontWeight: 'bold',
    },
    checkBoxButton: {
      display: 'flex',
      minWidth: 32,
      position: 'absolute',
      left: spacings.normal,
    },
    checkBoxChecked: {
      color: colorPalette.primaryColorGreen.primary,
    },
    checkBoxUnchecked: {
      color: '#8181A5',
    },
    moreButton: {
      display: 'flex',
      minWidth: 32,
      position: 'absolute',
      right: 12,
    },
    notificationBadge: {
      marginRight: 40,
    },
    plexColor: {
      width: 16,
      height: 16,
      borderRadius: 8,
      marginRight: 20,
      flexShrink: 0,
    },
  })
);

function MoreOptionsButton(props: IconButtonProps): React.ReactElement {
  return <IconButton {...props} size={'small'} />;
}

function moreOptions({
  currentUserRelation,
  onViewClick,
  onEditClick,
  onNewPostClick,
  onNewEventClick,
  onNewFactClick,
  onNewBoard,
  onOpenAnalytics,
  onLeaveClick,
  onNewCardClick,
}: {
  currentUserRelation: PlexUserRelationState;
  onViewClick: (id: string) => void;
  onEditClick: (id: string) => void;
  onNewPostClick: (id: string) => void;
  onNewEventClick: (id: string) => void;
  onNewFactClick: (id: string) => void;
  onNewBoard: (id: string) => void;
  onOpenAnalytics: (id: string) => void;
  onLeaveClick: (id: string) => void;
  onNewCardClick: (id: string) => void;
}): MenuOptionItem[] {
  const options: MenuOptionItem[] = [];

  options.push({ label: 'View', onClick: onViewClick });

  if (canManage(currentUserRelation)) {
    options.push({ label: 'Edit', onClick: onEditClick });
  }

  if (canLeave(currentUserRelation)) {
    options.push({
      label: currentUserRelation === PlexUserRelationState.Subscriber ? 'Unsubscribe' : 'Leave',
      onClick: onLeaveClick,
    });
  }

  if (canCreatePost(currentUserRelation)) {
    options.push({ label: 'Create Post', onClick: onNewPostClick });
    options.push({ label: 'Create Event', onClick: onNewEventClick });
  }

  if (canCreateFact(currentUserRelation)) {
    options.push({
      label: currentUserRelation === PlexUserRelationState.Subscriber ? 'Ask question' : 'Create FAQ',
      onClick: onNewFactClick,
    });
    options.push({
      label: 'Create Card',
      onClick: onNewCardClick,
    });
  }

  if (canCreateBoard(currentUserRelation)) {
    options.push({ label: 'Create Board', onClick: onNewBoard });
  }

  if (canViewPlexAnalytics(currentUserRelation)) {
    options.push({ label: 'Analytics', onClick: onOpenAnalytics });
  }

  return options;
}
